import { Fragment, useMemo, useState } from "react";
import { reasonLabel } from "../data/reasons";
import type { CodeCaseQuery, ErroredCode } from "../types";
import { CodeTableToolbar, fieldLabel, useCodeFilter } from "./codeTableToolbar";

/** Side colours: blue is the agent, amber is the coder — the same meaning as
 *  the outcome legend above the by-field table. */
export const AGENT_COLOR = "var(--ch-blue-600)";
export const CODER_COLOR = "var(--ch-amber-500)";

/** Two-segment agent/coder split for one errored code. */
export function SideSplitBar({
  agentWrong,
  coderWrong,
  wrong,
}: {
  agentWrong: number;
  coderWrong: number;
  wrong: number;
}) {
  if (wrong <= 0) return null;
  const segments = [
    { key: "agent", label: "Agent", n: agentWrong, color: AGENT_COLOR },
    { key: "coder", label: "Coder", n: coderWrong, color: CODER_COLOR },
  ];
  return (
    <span
      className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={segments
        .filter((s) => s.n > 0)
        .map((s) => `${s.label}: ${s.n}`)
        .join(", ")}
    >
      {segments.map((s) =>
        s.n > 0 ? (
          <span
            key={s.key}
            style={{ width: `${(s.n / wrong) * 100}%`, backgroundColor: s.color }}
            title={`${s.label}: ${s.n}`}
          />
        ) : null,
      )}
    </span>
  );
}

/** Whether the server sent the agent/coder split at all.
 *
 *  It is AUDITOR-ONLY: the errored-code floor is on the total, so a one-sided
 *  row would tell a reviewer who judged a case carrying that code which slot
 *  was the agent. Non-auditors get every split zeroed, so presence — not a
 *  role the client decided for itself — is what turns the side columns on. */
export function hasSideSplit(codes: ErroredCode[]): boolean {
  return codes.some((c) => c.agentWrong > 0 || c.coderWrong > 0);
}

type SortKey = "agentWrong" | "coderWrong" | "wrong";

/** Most-errored codes — the actionable table.
 *
 *  Rows arrive ranked and floored by the server (wrong desc, field, code).
 *  That order is kept until the reader sorts by a column; the sort is stable
 *  with the server's tiebreak so ties never reorder between renders.
 *
 *  Example ids and "View all cases" appear only when the server sent ids —
 *  it does so for auditors alone, because a case id beside agent/coder counts
 *  says which slot of that case is the agent. The side columns, the split bar
 *  and the side filter follow the same server signal: they render only when
 *  the response actually carries a split (see hasSideSplit). */
export function ErroredCodeTable({
  codes,
  onOpenCase,
  onOpenCodeCases,
}: {
  codes: ErroredCode[];
  onOpenCase?: (dcn: string) => void;
  onOpenCodeCases?: (q: CodeCaseQuery) => void;
}) {
  const split = hasSideSplit(codes);
  const filter = useCodeFilter(codes, (c) => ({ agent: c.agentWrong > 0, coder: c.coderWrong > 0 }));
  const [sort, setSort] = useState<{ key: SortKey; dir: "desc" | "asc" } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = useMemo(() => {
    if (!sort) return filter.shown;
    const sign = sort.dir === "desc" ? -1 : 1;
    return [...filter.shown].sort((a, b) =>
      a[sort.key] !== b[sort.key]
        ? sign * (a[sort.key] - b[sort.key])
        : a.field !== b.field
          ? a.field < b.field
            ? -1
            : 1
          : a.code < b.code
            ? -1
            : a.code > b.code
              ? 1
              : 0,
    );
  }, [filter.shown, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  const header = (key: SortKey, label: string) => (
    <th className="px-3 py-2 text-right font-medium">
      <button
        type="button"
        onClick={() => toggleSort(key)}
        aria-label={`Sort by ${label}`}
        aria-sort={sort?.key === key ? (sort.dir === "desc" ? "descending" : "ascending") : undefined}
        className="inline-flex items-center gap-1 uppercase hover:text-foreground"
      >
        {label}
        {sort?.key === key ? (sort.dir === "desc" ? " ▾" : " ▴") : ""}
      </button>
    </th>
  );

  return (
    <div className="flex flex-col gap-2.5">
      <CodeTableToolbar
        categories={filter.categories}
        counts={filter.counts}
        total={filter.total}
        category={filter.category}
        onCategory={filter.setCategory}
        search={filter.search}
        onSearch={filter.setSearch}
        side={filter.side}
        onSide={split ? filter.setSide : undefined}
      />
      <div className="max-h-[480px] overflow-auto rounded-coronis border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
              <th className="py-2 pl-4 pr-3 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Field</th>
              {split && <th className="min-w-[140px] px-3 py-2 font-medium">Side</th>}
              {split && header("agentWrong", "Agent")}
              {split && header("coderWrong", "Coder")}
              {header("wrong", "Wrong")}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const key = `${c.field}:${c.code}`;
              const open = expanded === key;
              const canDrill = c.exampleDcns.length > 0;
              const top = c.reasons[0]?.count ?? 0;
              return (
                <Fragment key={key}>
                  <tr
                    onClick={() => setExpanded(open ? null : key)}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50"
                  >
                    <td className="py-2.5 pl-4 pr-3 font-medium tabular-nums text-foreground">
                      {c.code}
                      <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">{open ? "▾" : "▸"}</span>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{fieldLabel(c.field)}</td>
                    {split && (
                      <td className="px-3 py-2.5">
                        <SideSplitBar agentWrong={c.agentWrong} coderWrong={c.coderWrong} wrong={c.wrong} />
                      </td>
                    )}
                    {split && <td className="px-3 py-2.5 text-right tabular-nums text-foreground">{c.agentWrong}</td>}
                    {split && <td className="px-3 py-2.5 text-right tabular-nums text-foreground">{c.coderWrong}</td>}
                    <td className="px-3 py-2.5 pr-4 text-right font-semibold tabular-nums text-foreground">{c.wrong}</td>
                  </tr>
                  {open && (
                    <tr className="border-b border-border bg-muted/30 last:border-0">
                      <td colSpan={split ? 6 : 3} className="px-4 py-3">
                        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                              Why it was judged wrong
                            </span>
                            {c.reasons.length === 0 ? (
                              <span className="text-[12.5px] text-muted-foreground">No reason recorded.</span>
                            ) : (
                              <ul className="flex flex-col gap-1">
                                {c.reasons.map((r) => (
                                  <li key={r.reason} className="flex flex-col gap-0.5">
                                    <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
                                      <span className="text-foreground">{reasonLabel(r.reason)}</span>
                                      <span className="shrink-0 tabular-nums text-muted-foreground">{r.count}</span>
                                    </div>
                                    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                                      <div
                                        className="h-full rounded-full bg-coronis-teal-500"
                                        style={{ width: `${top > 0 ? (r.count / top) * 100 : 0}%` }}
                                      />
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {canDrill && (
                            <div className="flex flex-col items-start gap-1.5">
                              <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                                Examples
                              </span>
                              <div className="flex flex-wrap gap-1.5">
                                {c.exampleDcns.map((dcn) => (
                                  <button
                                    key={dcn}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenCase?.(dcn);
                                    }}
                                    className="rounded-coronis border border-border bg-card px-2 py-0.5 text-[12px] font-medium tabular-nums text-foreground hover:bg-muted"
                                  >
                                    {dcn}
                                  </button>
                                ))}
                              </div>
                              {onOpenCodeCases && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenCodeCases({ mode: "errored", field: c.field, code: c.code });
                                  }}
                                  className="text-[12.5px] font-medium text-coronis-blue-600 hover:underline"
                                >
                                  View all cases →
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {!split && (
        // Say the column is missing rather than let the table read as though
        // the study never attributed a code to a side. Wording deliberately
        // states the RULE, not the reader's own role — the client has no
        // business telling anyone what they are.
        <p className="text-[11.5px] leading-snug text-muted-foreground">
          Which side produced each errored code is shown to auditors only.
        </p>
      )}
    </div>
  );
}
